// Route Code Edited 10/14/2026
/**
 * Shows cart subtotal + Route protection cost on Route's cloned checkout
 * button in the UpCart drawer (e.g. "Checkout • $160.33" for $149.00 + $11.33).
 *
 * UpCart and Route update their values in either order, so the total is only
 * recomputed once cart and Route quote requests have settled; until then the
 * last correct total stays on the button.
 *
 *   <script src="{{ 'route-checkout-subtotal-update.js' | asset_url }}" defer></script>
 */
(function () {
  if (window.__routeCheckoutSubtotalUpdate) return;
  window.__routeCheckoutSubtotalUpdate = true;

  const ROOT_SELECTOR      = '.upcart-checkout-button-container';
  const CLONE_SELECTOR     = '[data-route-cloned-button="true"]';
  const ORIGINAL_SELECTOR  = '.upcart-checkout-button:not([data-route-cloned-button])';
  const COST_SELECTOR      = '.pw-coverage-description';
  const ROUTE_ROOT_ID      = 'route-widget-observer-root';
  const HOLD_CLASS         = 'route-clone-active';
  const CART_URL           = /\/cart(\.js|\/(add|change|update|clear)(\.js)?)(\?|$)/;
  const CART_MUTATION_URL  = /\/cart\/(add|change|update|clear)/;
  const QUOTE_URL          = /route\.com\/.*quotes?(\?|\/|$)/i;
  const TOTAL_FIELD        = /(^|[^a-z_])(updates|discount)([^a-z_]|$)/i;
  const QUOTE_RENDER_MS    = 50;
  const MAX_WAIT_MS        = 1500;
  const REMOUNT_GRACE_MS   = 1500;
  const ROUTE_TIMEOUT_MS   = 10000;
  const RETRY_MS           = 300;
  const MAX_RETRIES        = 10;

  let clone = null, original = null, lastKnownGood = null;
  let guardObserver = null, originalObserver = null, costObserver = null, bodyObserver = null;
  let waitTimer = null, retryTimer = null, graceTimer = null, retries = 0, scanQueued = false;

  const net = {
    mutations: 0, lastMutation: -Infinity, cart: null, cartStale: false,
    cartUsable: null, costStaleSince: -Infinity,
    quotes: 0, lastQuoteStart: -Infinity, lastQuoteEnd: -Infinity,
  };

  const safely = (fn) => { try { return fn(); } catch (e) { return null; } };
  const near = (a, b) => Math.abs(a - b) < 0.005;

  // ─── Money ──────────────────────────────────────────────────────

  function numericToken(text) {
    const m = text.match(/\d[\d.,]*\d|\d/);
    return m ? m[0] : null;
  }

  // A separator followed by exactly three digits is a thousands separator.
  function decimalIndex(token) {
    const i = Math.max(token.lastIndexOf('.'), token.lastIndexOf(','));
    return i !== -1 && token.length - i - 1 === 3 ? -1 : i;
  }

  function parseToken(token) {
    if (!token) return null;
    const i = decimalIndex(token);
    const value = parseFloat(i === -1
      ? token.replace(/[.,]/g, '')
      : token.slice(0, i).replace(/[.,]/g, '') + '.' + token.slice(i + 1));
    return isNaN(value) ? null : value;
  }

  // Formats value with the same separators the theme's token uses.
  function formatLike(token, value) {
    const i = decimalIndex(token);
    const dec = i === -1 ? (token.includes('.') ? ',' : '.') : token[i];
    const [int, frac] = value.toFixed(2).split('.');
    return int.replace(/\B(?=(\d{3})+(?!\d))/g, dec === '.' ? ',' : '.') + dec + frac;
  }

  // ─── DOM reads ──────────────────────────────────────────────────

  function priceNode(button) {
    if (!button) return null;
    const walker = document.createTreeWalker(button, NodeFilter.SHOW_TEXT);
    for (let n = walker.nextNode(); n; n = walker.nextNode()) {
      if (numericToken(n.textContent)) return n;
    }
    return null;
  }

  function routeCost() {
    const el = document.querySelector(COST_SELECTOR);
    return el ? parseToken(numericToken(el.textContent)) : null;
  }

  function findOriginal(btn) {
    const root = btn.closest(ROOT_SELECTOR);
    return (root && root.querySelector(ORIGINAL_SELECTOR)) || document.querySelector(ORIGINAL_SELECTOR);
  }

  function subtotalInfo() {
    // UpCart replaces its button on re-render; follow the live one.
    if ((!original || !original.isConnected) && clone) {
      const latest = findOriginal(clone);
      if (latest && latest !== original) {
        original = latest;
        watchOriginal();
      }
    }
    const node = priceNode(original);
    if (!node) return null;
    const text = node.textContent;
    const token = numericToken(text);
    const value = parseToken(token);
    if (value === null) return null;
    const at = text.indexOf(token);
    return { value, token, prefix: text.slice(0, at), suffix: text.slice(at + token.length) };
  }

  // ─── Network ────────────────────────────────────────────────────

  // Only quantity and discount changes move the total; attribute/note-only
  // /cart/update.js calls don't. Unreadable bodies are assumed to.
  function canChangeTotal(url, body) {
    try {
      if (TOTAL_FIELD.test(decodeURIComponent(url))) return true;
      if (body == null) return false;
      if (typeof body === 'string') return TOTAL_FIELD.test(decodeURIComponent(body));
      if (body instanceof URLSearchParams || body instanceof FormData) {
        for (const key of body.keys()) if (TOTAL_FIELD.test(key)) return true;
        return false;
      }
    } catch (e) {}
    return true;
  }

  function requestKind(url, body) {
    if (QUOTE_URL.test(url)) return 'quote';
    if (!CART_URL.test(url)) return null;
    if (!CART_MUTATION_URL.test(url)) return 'cart';
    if (/\/cart\/update/.test(url) && !canChangeTotal(url, body)) return 'attributes';
    return 'mutation';
  }

  function onRequestStart(kind) {
    if (kind === 'mutation') {
      net.mutations += 1;
      net.lastMutation = performance.now();
      net.cartStale = true;
    } else if (kind === 'quote') {
      net.quotes += 1;
      net.lastQuoteStart = performance.now();
    }
  }

  function onRequestEnd(kind, json) {
    if (kind === 'mutation') net.mutations = Math.max(0, net.mutations - 1);
    if (kind === 'quote') {
      net.quotes = Math.max(0, net.quotes - 1);
      net.lastQuoteEnd = performance.now();
      setTimeout(onNetChange, QUOTE_RENDER_MS + 5);
    } else if (json && typeof json.total_price === 'number' && Array.isArray(json.items)) {
      const total = json.total_price / 100;
      // The total moved, so Route's cost is stale until it re-quotes.
      if (!net.cart || net.cart.total !== total) net.costStaleSince = net.lastMutation;
      net.cart = { total, items: json.items_subtotal_price / 100 };
      net.cartStale = false;
    }
    onNetChange();
  }

  function onNetChange() {
    if (waitTimer) requestReconcile();
  }

  // Our bookkeeping is wrapped in safely() so it can never break the
  // merchant's own requests.
  function hookNetwork() {
    const origFetch = window.fetch;
    if (origFetch) {
      window.fetch = function (input, init) {
        const kind = safely(() => requestKind(
          typeof input === 'string' ? input : (input && input.url) || String(input || ''),
          init && init.body != null ? init.body : (typeof input === 'string' || input instanceof URL ? null : input)
        ));
        if (!kind) return origFetch.apply(this, arguments);
        safely(() => onRequestStart(kind));
        return origFetch.apply(this, arguments).then(
          (res) => {
            safely(() => {
              if (kind === 'quote') return onRequestEnd(kind, null);
              res.clone().json()
                .then((json) => safely(() => onRequestEnd(kind, json)))
                .catch(() => safely(() => onRequestEnd(kind, null)));
            });
            return res;
          },
          (err) => {
            safely(() => onRequestEnd(kind, null));
            throw err;
          }
        );
      };
    }

    const origOpen = XMLHttpRequest.prototype.open;
    const origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (_method, url) {
      this.__routeUrl = String(url);
      return origOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function (body) {
      const xhr = this;
      const kind = safely(() => requestKind(xhr.__routeUrl || '', body === undefined ? null : body));
      if (kind) {
        safely(() => onRequestStart(kind));
        xhr.addEventListener('loadend', () => safely(() => onRequestEnd(kind,
          kind === 'quote' ? null
            : xhr.responseType === 'json' ? xhr.response
            : safely(() => JSON.parse(xhr.responseText)))));
      }
      return origSend.apply(this, arguments);
    };
  }

  // Why the subtotal or cost can't be trusted yet, or null when both are current.
  function pendingReason() {
    if (net.mutations > 0 || net.cartStale) return 'cart';
    if (net.quotes > 0 || net.lastQuoteStart < net.costStaleSince) return 'quote';
    if (performance.now() - net.lastQuoteEnd < QUOTE_RENDER_MS) return 'quote';
    const info = subtotalInfo();
    if (info && net.cart) {
      if (near(info.value, net.cart.total) || near(info.value, net.cart.items)) net.cartUsable = true;
      else if (net.cartUsable !== false) return 'subtotal';
    }
    return null;
  }

  // ─── Reconcile ──────────────────────────────────────────────────

  function requestReconcile() {
    if (!clone) return;
    if (!pendingReason()) {
      clearTimeout(waitTimer);
      waitTimer = null;
      return reconcile();
    }
    if (waitTimer) return;
    waitTimer = setTimeout(() => {
      waitTimer = null;
      // Never matched the cart total even after waiting: UpCart shows some
      // other figure, so stop comparing against it.
      if (pendingReason() === 'subtotal' && net.cartUsable === null) net.cartUsable = false;
      reconcile();
    }, MAX_WAIT_MS);
  }

  function commit(node, text) {
    lastKnownGood = text; // set first so the guard ignores our own write
    node.textContent = text;
    clone.setAttribute('data-route-price-corrected', '');
    clone.setAttribute('aria-label', text.replace(/\s+/g, ' ').trim());
  }

  function reconcile() {
    const node = clone && priceNode(clone);
    if (!node) return;
    const cost = routeCost();
    const info = cost === null ? null : subtotalInfo();
    if (!info) {
      if (++retries <= MAX_RETRIES) {
        clearTimeout(retryTimer);
        retryTimer = setTimeout(requestReconcile, RETRY_MS);
      }
      return;
    }
    retries = 0;
    const text = info.prefix + formatLike(info.token, info.value + cost) + info.suffix;
    if (text !== lastKnownGood || node.textContent !== text) commit(node, text);
  }

  // ─── Observers ──────────────────────────────────────────────────

  function watchOriginal() {
    if (originalObserver) originalObserver.disconnect();
    if (!original) return;
    originalObserver = new MutationObserver(requestReconcile);
    originalObserver.observe(original, { characterData: true, childList: true, subtree: true });
  }

  function watchCost() {
    if (costObserver) costObserver.disconnect();
    const el = document.querySelector(COST_SELECTOR);
    if (!el) return;
    costObserver = new MutationObserver(requestReconcile);
    costObserver.observe(el, { characterData: true, childList: true, subtree: true });
  }

  // Route resets the clone's text whenever the original changes; put the
  // last correct total back before the browser paints.
  function guardClone() {
    guardObserver = new MutationObserver(() => {
      const node = lastKnownGood !== null && priceNode(clone);
      if (node && node.textContent !== lastKnownGood) node.textContent = lastKnownGood;
    });
    guardObserver.observe(clone, { subtree: true, childList: true, characterData: true });
  }

  function dropClone() {
    [guardObserver, originalObserver].forEach((o) => o && o.disconnect());
    [waitTimer, retryTimer].forEach((t) => clearTimeout(t));
    clone = original = guardObserver = originalObserver = waitTimer = retryTimer = null;
    retries = 0;
  }

  // Keeps UpCart's own (bare-subtotal) button hidden while Route remounts
  // its clone after an UpCart re-render, unless Route never comes back.
  function setHold(on) {
    clearTimeout(graceTimer);
    graceTimer = null;
    document.documentElement.classList.toggle(HOLD_CLASS, on);
  }

  function adoptClone(btn) {
    dropClone();
    clone = btn;
    original = findOriginal(btn);
    guardClone();
    watchOriginal();
    // A rebuilt clone shows Route's bare subtotal; show the last total at once.
    const node = priceNode(btn);
    if (lastKnownGood !== null && node && node.textContent !== lastKnownGood) commit(node, lastKnownGood);
    requestReconcile();
  }

  function scan() {
    if (clone && clone.isConnected) return;
    const btn = document.querySelector(CLONE_SELECTOR);
    if (btn) {
      setHold(true);
      adoptClone(btn);
    } else {
      if (!graceTimer && document.documentElement.classList.contains(HOLD_CLASS)) {
        graceTimer = setTimeout(() => setHold(false), REMOUNT_GRACE_MS);
      }
      if (clone) dropClone();
    }
  }

  // Reports whether a selector's element was replaced since the last call.
  function tracker(selector) {
    let current = null;
    return () => {
      if (current && current.isConnected) return false;
      const latest = document.querySelector(selector);
      const changed = latest !== current;
      current = latest;
      return changed;
    };
  }

  const rootChanged = tracker(ROOT_SELECTOR);
  const costChanged = tracker(COST_SELECTOR);

  function start() {
    if (bodyObserver) bodyObserver.disconnect();
    rootChanged();
    costChanged();
    watchCost();
    bodyObserver = new MutationObserver((mutations) => {
      // Cheap type check only; Route's re-renders can emit huge batches.
      if (scanQueued || !mutations.some((m) => m.type === 'childList')) return;
      scanQueued = true;
      queueMicrotask(() => {
        scanQueued = false;
        if (rootChanged() && clone) dropClone();
        const costMoved = costChanged();
        if (costMoved) watchCost();
        scan();
        if (costMoved && document.querySelector(COST_SELECTOR)) requestReconcile();
      });
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
    scan();
  }

  function stop() {
    [bodyObserver, costObserver].forEach((o) => o && o.disconnect());
    bodyObserver = costObserver = lastKnownGood = null;
    dropClone();
    setHold(false);
  }

  // Waits for Route (up to ROUTE_TIMEOUT_MS), then for UpCart's drawer, which
  // may not render until it is first opened.
  function init() {
    let routeSeen = !!document.getElementById(ROUTE_ROOT_ID);
    if (routeSeen && document.querySelector(ROOT_SELECTOR)) return start();
    let timeout = null;
    const waiter = new MutationObserver(() => {
      if (!routeSeen) {
        if (!document.getElementById(ROUTE_ROOT_ID)) return;
        routeSeen = true;
        clearTimeout(timeout);
      }
      if (!document.querySelector(ROOT_SELECTOR)) return;
      waiter.disconnect();
      start();
    });
    waiter.observe(document.body, { childList: true, subtree: true });
    if (!routeSeen) timeout = setTimeout(() => waiter.disconnect(), ROUTE_TIMEOUT_MS);
  }

  hookNetwork();
  const style = document.createElement('style');
  style.textContent = 'html.' + HOLD_CLASS + ' ' + ROOT_SELECTOR + ' ' + ORIGINAL_SELECTOR + '{visibility:hidden!important}';
  document.head.appendChild(style);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  window.addEventListener('pagehide', stop);
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    stop();
    start();
  });
})();
// End Route Code
